import { StoreApi, create, useStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  createJSONStorage,
  devtools,
  persist,
  subscribeWithSelector,
} from 'zustand/middleware';
import {
  createLocationSlice,
  createSearchSlice,
  createTopicLayerSlice,
  createBasemapSlice,
  createExtraLayersSlice,
  createDrawSlice,
  createAuthSlice,
  defaultSearchSliceProps,
  defaultTopicLayerProps,
  defaultExtraSliceProps,
  defaultDrawSliceProps as ktmapDefaultDrawSliceProps,
  defaultAuthSliceProps,
  createLayerStateSlice,
  defaultBasemapProps as ktmapDefaultBasemapProps,
  createGeoLocationSlice,
  defaultGeoLocationSliceProps,
  DrawTool,
} from '@agissc/ktag-map';
import { TemporalState, temporal } from 'zundo';
import { IAppStore } from './IAppStore';
import createActiveToolSlice, {
  defaultActiveToolProps,
} from './tools/ActiveToolSliceCreator';
import createUIStateSlice, { defaultUIStateProps } from './UIStore';
import createTopicLayerFilterSlice, {
  defaultTopicLayerFilterProps,
} from './tools/mapContent/store/TopicLayerFilterStore';
import createBasemapCategorySlice, {
  defaultBasemapCategoryProps,
} from './tools/mapContent/store/BasemapStore';
import createTopicLayerSettingsSlice, {
  defaultTopicLayerSettingsProps,
} from './tools/mapContent/store/TopicLayerSettingsStore';
import createConfigurationSlice from './tools/configuration/ConfigurationSliceCreator';
import createPrintSlice, { defaultPrintSliceProps } from './tools/print/store/PrintStore';
import createIdentifySlice, {
  defaultIdentifyProps,
} from './tools/identify/store/IdentifyStore';
import createAttributeSearchSlice, {
  defaultAttributeSearchSliceProps,
} from './tools/mapContent/store/AttributeSearchStore';
import persistentStorage from './utils/persistentStorage';
import appStoreEquality from './utils/appStoreEquality';
import appStorePartialize from './utils/appStorePartialize';
import appStoreMigration from './utils/appStoreMigration';

const defaultLocationProps = {
  location: {
    extent: {
      xMin: 2623613,
      yMin: 1221875,
      xMax: 2676113,
      yMax: 1274125,
    },
    rotation: 0,
  },
};

const defaultBasemapProps = {
  ...ktmapDefaultBasemapProps,
  selectedBasemapId: 'base_landeskarten_sw::topicmaps.geo.ag.ch',
};

const defaultDrawSliceProps = {
  ...ktmapDefaultDrawSliceProps,
  activeDrawTool: 'line' as DrawTool,
};

const useAppState = create<
  IAppStore,
  [
    ['zustand/persist', IAppStore],
    ['temporal', StoreApi<TemporalState<IAppStore>>],
    ['zustand/devtools', never],
    ['zustand/subscribeWithSelector', never],
    ['zustand/immer', never],
  ]
>(
  persist(
    temporal(
      devtools(
        subscribeWithSelector(
          immer((set, get, api) => ({
            ...createUIStateSlice(set, get, api),
            ...createActiveToolSlice(set, get, api),
            ...createLocationSlice(defaultLocationProps)(set, get, api),
            ...createSearchSlice(set, get, api),
            ...createBasemapSlice(defaultBasemapProps)(set, get, api),
            ...createBasemapCategorySlice(set, get, api),
            ...createTopicLayerSlice()(set, get, api),
            ...createTopicLayerFilterSlice(set, get, api),
            ...createTopicLayerSettingsSlice(set, get, api),
            ...createConfigurationSlice(set, get, api),
            ...createExtraLayersSlice()(set, get, api),
            ...createDrawSlice(set, get, api),
            ...createPrintSlice(set, get, api),
            ...createIdentifySlice(set, get, api),
            ...createAuthSlice(set, get, api),
            ...createAttributeSearchSlice(set, get, api),
            ...createLayerStateSlice(set, get, api),
            ...createGeoLocationSlice(defaultGeoLocationSliceProps)(set, get, api),
          })),
        ),
      ),
      {
        equality: appStoreEquality,
        partialize: appStorePartialize,
      },
    ),
    {
      name: 'agisviewer',
      storage: createJSONStorage<IAppStore>(() => persistentStorage),
      version: 0.2,
      migrate: appStoreMigration,
    },
  ),
);

const initialeState = {
  ...defaultUIStateProps,
  ...defaultActiveToolProps,
  ...defaultLocationProps,
  ...defaultSearchSliceProps,
  ...defaultBasemapProps,
  ...defaultBasemapCategoryProps,
  ...defaultTopicLayerProps,
  ...defaultTopicLayerSettingsProps,
  ...defaultExtraSliceProps,
  ...defaultDrawSliceProps,
  ...defaultPrintSliceProps,
  ...defaultAuthSliceProps,
  ...defaultTopicLayerFilterProps,
  ...defaultIdentifyProps,
  ...defaultAttributeSearchSliceProps,
  ...defaultGeoLocationSliceProps,
};

export const resetStore = () =>
  useAppState.setState({
    ...initialeState,
    displayResetNotification: true,
  });

export const useTemporalStore = () => useStore(useAppState.temporal);

export default useAppState;
